import { i18n } from '@/i18n';
import {
	postLoginAPI,
	postSocialLoginAPI,
	postSocialRegisterAPI,
	postSocialVerifyAPI,
	postSocialResendAPI,
	deleteSocialLoginAPI,
} from '@/services/api/auth.api';
import {
	LOGIN_REQUEST,
	LOGIN_SUCCESS,
	LOGIN_FAILURE,
	LOGIN_CLEAR_ERROR,
	LOGOUT,
	SET_USERNAME,
	REGISTER_SOCIAL_REQUEST,
	REGISTER_SOCIAL_SUCCESS,
	REGISTER_SOCIAL_FAILURE,
	REGISTER_SOCIAL_CLEAR_ERROR,
	VERIFY_SOCIAL_REQUEST,
	VERIFY_SOCIAL_SUCCESS,
	VERIFY_SOCIAL_FAILURE,
	VERIFY_SOCIAL_CLEAR_ERROR,
	RESEND_SOCIAL_REQUEST,
	RESEND_SOCIAL_SUCCESS,
	RESEND_SOCIAL_FAILURE,
	TWO_FACTOR_FAILURE,
	TWO_FACTOR_CLEAR_ERROR,
} from '../types';
import { transformedUser } from '../../assets/js/transform/user';
import { pathOr } from '../../assets/js/helpers';
import errorMessage from '../../enums/errorMessage';

const defaultUser = {};
const defaultSocialUser = {};
const defaultResend = {
	isLoading: false,
	error: false,
	errorMessage: null,
	requestExpiredTime: null,
};
const defaultTwoFactor = {
	isLoading: false,
	error: false,
	errorMessage: null,
	redirect: null,
};

const tokenAtInit = localStorage.getItem('token');

export const getters = {
	getUsername(state) {
		return state.user.username || null;
	},
	transformedUser(state) {
		return transformedUser(state.user);
	},
};

export const mutations = {
	[LOGIN_REQUEST](state) {
		state.isLoading = true;
		state.isLoggedIn = false;
		state.error = false;
		state.errorMessage = null;
	},
	[LOGIN_SUCCESS](state) {
		state.isLoading = false;
		state.isLoggedIn = true;
	},
	[LOGIN_FAILURE](state, { message, retryAfterTime } = {}) {
		state.isLoading = false;
		state.isLoggedIn = false;
		state.error = true;
		state.errorMessage = message;
		state.nextLoginAllowedAt = retryAfterTime;
	},

	[LOGOUT](state) {
		state.isLoggedIn = false;
		state.user = defaultUser;
	},

	[LOGIN_CLEAR_ERROR](state) {
		state.error = false;
		state.errorMessage = null;
	},
	[REGISTER_SOCIAL_REQUEST](state) {
		state.isLoading = true;
		state.error = false;
	},
	[REGISTER_SOCIAL_SUCCESS](state) {
		state.isLoading = false;
	},
	[REGISTER_SOCIAL_FAILURE](state, message) {
		state.isLoading = false;
		state.error = true;
		state.errorMessage = message;
	},
	[REGISTER_SOCIAL_CLEAR_ERROR](state) {
		state.error = false;
		state.errorMessage = null;
	},
	[SET_USERNAME](state, username) {
		state.user = {
			...state.user,
			username,
		};
	},
	[VERIFY_SOCIAL_REQUEST](state) {
		state.isLoading = true;
		state.error = false;
	},
	[VERIFY_SOCIAL_SUCCESS](state) {
		state.isLoading = false;
	},
	[VERIFY_SOCIAL_FAILURE](state, message) {
		state.isLoading = false;
		state.error = true;
		state.errorMessage = message;
	},
	[VERIFY_SOCIAL_CLEAR_ERROR](state) {
		state.error = false;
		state.errorMessage = null;
	},
	[RESEND_SOCIAL_REQUEST](state) {
		state.resend.isLoading = true;
		state.resend.error = false;
		state.resend.errorMessage = null;
	},
	[RESEND_SOCIAL_SUCCESS](state, requestExpiredTime) {
		state.resend.isLoading = false;
		state.resend.requestExpiredTime = requestExpiredTime;
	},
	[RESEND_SOCIAL_FAILURE](state, { message, requestExpiredTime }) {
		state.resend.isLoading = false;
		state.resend.error = true;
		state.resend.errorMessage = message;
		state.resend.requestExpiredTime = requestExpiredTime;
	},
	[TWO_FACTOR_FAILURE](state, { message, redirect }) {
		state.twoFactor.isLoading = false;
		state.twoFactor.error = true;
		state.twoFactor.errorMessage = message;
		state.twoFactor.redirect = redirect;
	},
	[TWO_FACTOR_CLEAR_ERROR](state) {
		state.twoFactor.error = false;
		state.twoFactor.errorMessage = null;
	},
};

export const actions = {
	async postLogin({ commit }, { username = null, password = null }) {
		try {
			commit(LOGIN_REQUEST);

			const { data } = await postLoginAPI({
				username,
				password,
			});

			commit(LOGIN_SUCCESS);

			// Keep token, refreshToken to local storeage
			const { access_token: accessToken = null, refresh_token: refreshToken = null } = data;

			if (accessToken) {
				localStorage.setItem('token', accessToken);
			}
			if (refreshToken) {
				localStorage.setItem('refreshToken', refreshToken);
			}
		} catch (error) {
			const errorStatus = pathOr(null, ['response', 'status'])(error);
			let message;

			let retryAfterTime;

			switch (errorStatus) {
				case 400:
					message = errorMessage.LOGIN_FAILURE;
					break;

				case 403:
					message = errorMessage.LOGIN_ACCOUNT_INACTIVE;
					break;

				case 429:
					message = errorMessage.LOGIN_TOO_MANY;
					retryAfterTime = pathOr(null, ['response', 'data', 'retry_after_time'])(error);
					break;

				default:
					message = errorMessage.UNEXPECTED_ERROR;
					break;
			}

			commit(LOGIN_FAILURE, {
				message,
				retryAfterTime: retryAfterTime ?? 0,
			});
		}
	},
	async logout({ commit, dispatch }) {
		try {
			commit(LOGOUT);
			await deleteSocialLoginAPI();
			dispatch('profile/clearProfile', null, { root: true });

			localStorage.removeItem('token');
			localStorage.removeItem('refreshToken');
		} catch (error) {
			// noop

		} finally {
			commit(LOGOUT);
			localStorage.clear();
		}
	},
	async setTwoFactorError({ commit }, payload) {
		commit(TWO_FACTOR_FAILURE, payload);
	},
	clearError({ commit }) {
		commit(LOGIN_CLEAR_ERROR);
	},
	setUsername({ commit }) {
		const username = localStorage.getItem('username');
		commit(SET_USERNAME, username);
	},
	async postSocialLogin({ commit }, { token }) {
		commit(LOGIN_REQUEST);
		const { data } = await postSocialLoginAPI({
			crmToken: token,
		});
		commit(LOGIN_SUCCESS);
		// Keep token, refreshToken to local storeage
		const { access_token: accessToken = null, refresh_token: refreshToken = null } = data;
		if (accessToken) {
			// ?? FYI: in case of social login, username is not available so we move it to after get profile
			localStorage.setItem('token', accessToken);
		}
		if (refreshToken) {
			localStorage.setItem('refreshToken', refreshToken);
		}
	},
	async postSocialRegister({ commit }, { token, payload }) {
		try {
			commit(REGISTER_SOCIAL_REQUEST);
			await postSocialRegisterAPI(token, payload);
			commit(REGISTER_SOCIAL_SUCCESS);
		} catch (error) {
			const errorStatus = pathOr(null, ['response', 'status'])(error);
			const apiErrorMessage = pathOr(null, ['response', 'data', 'message'])(error);
			let message;
			switch (errorStatus) {
				case 400:
					message = i18n.t(errorMessage.REGISTER_SOCIAL_FAILURE);
					break;
				case 422:
					message = apiErrorMessage;
					break;
				default:
					message = i18n.t(errorMessage.UNEXPECTED_ERROR);
					break;
			}
			commit(REGISTER_SOCIAL_FAILURE, message);
		}
	},
	async postSocialVerify({ commit }, payload) {
		try {
			commit(VERIFY_SOCIAL_REQUEST);

			await postSocialVerifyAPI(payload);

			localStorage.removeItem('verified');

			commit(VERIFY_SOCIAL_SUCCESS);
		} catch (error) {
			const errorStatus = pathOr(null, ['response', 'status'])(error);
			const apiErrorMessage = pathOr(null, ['response', 'data', 'message'])(error);

			let message;
			switch (errorStatus) {
				case 400:
					message = errorMessage.REGISTER_SOCIAL_FAILURE;
					break;
				case 403:
				case 422:
				case 429:
					// Wrong / expired code or too many attempts:
					// show what the API says, inline under the input.
					message = apiErrorMessage || errorMessage.UNEXPECTED_ERROR;
					break;
				default:
					message = errorMessage.UNEXPECTED_ERROR;
					break;
			}

			commit(VERIFY_SOCIAL_FAILURE, message);
		}
	},
	async postSocialResend({ commit, dispatch }) {
		try {
			commit(RESEND_SOCIAL_REQUEST);

			const { data } = await postSocialResendAPI();

			dispatch('toast/showToast', {
				content: i18n.t('global.successMessageResendOTP'),
				header: i18n.t('global.successMessageTitleResendOTP'),
				type: 'success',
			}, { root: true });

			const apiRequestExpiredTime = pathOr(null, ['request_expired_at'])(data);

			commit(RESEND_SOCIAL_SUCCESS, apiRequestExpiredTime);
		} catch (error) {
			const errorStatus = pathOr(null, ['response', 'status'])(error);
			const apiErrorMessage = pathOr(null, ['response', 'data', 'message'])(error);
			const apiRequestExpiredTime = pathOr(null, ['response', 'data', 'request_expired_time'])(error);

			let message;
			switch (errorStatus) {
				case 400:
					message = errorMessage.RESEND_OTP_FAILURE;
					break;
				case 403:
				case 422:
				case 429:
					message = apiErrorMessage;
					break;
				default:
					message = errorMessage.UNEXPECTED_ERROR;
					break;
			}


			// The global interceptor stays quiet on 2FA endpoints now,
			// so the resend failure is surfaced here instead.
			dispatch('toast/showToast', {
				content: apiErrorMessage || i18n.t(message),
				header: i18n.t('global.errorMessageTitleResendOTP'),
				type: 'danger',
			}, { root: true });

			commit(RESEND_SOCIAL_FAILURE, { message, requestExpiredTime: apiRequestExpiredTime });
		}
	},

	clearLoginSocialError({ commit }) {
		commit(LOGIN_FAILURE);
	},
	clearRegisterSocialError({ commit }) {
		commit(REGISTER_SOCIAL_CLEAR_ERROR);
	},
	clearVerifySocialError({ commit }) {
		commit(VERIFY_SOCIAL_CLEAR_ERROR);
	},
	clearTwoFactorError({ commit }) {
		commit(TWO_FACTOR_CLEAR_ERROR);
	},
};

export default {
	namespaced: true,
	state: {
		isLoading: false,
		isLoggedIn: !!tokenAtInit,
		error: false,
		errorMessage: null,
		user: defaultUser,
		socialUser: defaultSocialUser,
		resend: defaultResend,
		twoFactor: defaultTwoFactor,
		nextLoginAllowedAt: 0,
	},
	getters,
	mutations,
	actions,
};
